import { dateHelper } from './dateHelper';

class DtaCalendar {
    constructor(initElement, options = {}) {
        this.options = Object.assign({
            totalCalendar: 1,
            totalYear: 2,
            addDay: 0,
            locale: 'en-US',
            btnWidth: null,
            readOnly: true,
            monthNames: [],
            weekDays: [],
            minDate: null,
            maxDate: null,
            disableDates: [],
            extraParams: {},
            events: {
                afterInit: (event) => {},
                next: (event) => {},
                previous: (event) => {},
                select: (event) => {},
                change: (event) => {},
                beforeResize: (event) => {},
                afterResize: (event) => {},
                changeTotalCalendar: (event) => {}
            },
        }, options);

        this.currentDate = new Date();
        this.initElement = initElement;

        this.options.monthNames = this.getMonthNames(this.options.locale);
        this.options.weekDays = this.getWeekDays(this.options.locale);
        this.options.btnWidth = this.getButtonWidth();

        this.inputSelectYear = null;
        this.inputSelectMonth = null;
        this.selectDate = null;
        this.controllerElement = null;
        this.calendarElement = null;
        this.buttonNext = null;
        this.buttonPrev = null;
        this.minYear = null;
        this.maxYear = null;
        this.minDate = null;
        this.maxDate = null;
        this.calendars = [];

        this.init();
        window.addEventListener('resize', this.debouncedResize.bind(this));
    }

    init() {
        const calendarContainer = document.createElement('div');
        calendarContainer.classList.add('dta-calendar');

        this.createSelectYear();
        this.selectMonth(parseInt(this.inputSelectYear.value));

        this.controllerElement = this.createController();
        this.calendarElement = document.createElement('div');
        this.calendarElement.classList.add('calendars',  'd-flex',  'align-content-center');

        this.setCalendar(parseInt(this.inputSelectYear.value), parseInt(this.inputSelectMonth.value));

        calendarContainer.appendChild(this.controllerElement);
        calendarContainer.appendChild(this.calendarElement);

        this.initElement.innerHTML = '';
        this.initElement.appendChild(calendarContainer);
        if(typeof this.options.events.afterInit === 'function') {
            this.options.events.afterInit({
                thisObj : this,
                params : this.getPayload()
            });
        }
    }

    debouncedResize(event) {
        if(this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
            this.eventResize(event);
        }, 1000);
    }
    eventResize(event) {
        const totalCalendar = this.options.totalCalendar;
        if(typeof this.options.events.beforeResize === 'function') {
            this.options.events.beforeResize({
                thisObj : this,
                params : this.getPayload()
            });
        }

        this.options.btnWidth = this.getButtonWidth();
        this.setCalendar(parseInt(this.inputSelectYear.value), parseInt(this.inputSelectMonth.value));
        if(typeof this.options.events.afterResize === 'function') {
            this.options.events.afterResize({
                thisObj : this,
                params : this.getPayload()
            });
        }

        if(totalCalendar !== this.options.totalCalendar) {
            if(typeof this.options.events.changeTotalCalendar === 'function') {
                this.calendars.forEach(_calendar => {
                    if(this.options.totalCalendar>1) {
                        _calendar.element.children[0].classList.remove('d-none');
                    } else {
                        _calendar.element.children[0].classList.add('d-none');
                    }
                    _calendar.weeks.forEach((week) => {
                        week.forEach((day) => {
                            day.button.style.width = `${this.options.btnWidth}px`;
                        })
                    });
                });
                this.options.events.changeTotalCalendar({
                    thisObj : this,
                    params : this.getPayload(),
                    oldValue: totalCalendar,
                    newValue: this.options.totalCalendar
                });
            }
        }
    }

    getButtonWidth() {
        const total = Math.max(1, this.options.totalCalendar);
        const padYBorder = ((5 * 7) * total) + (10 * (total - 1));
        const clientW = this.initElement ? this.initElement.clientWidth : 0;
        const maxWidth = Math.max(0, clientW - padYBorder);
        if (maxWidth <= 0) return 38;
        const colCount = 7 * total;
        const width = (maxWidth - (maxWidth % colCount)) / colCount;
        return Math.max(28, width);
    }

    getMonthNames(locale) {
        try {
            const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
            return Array.from({ length: 12 }, (_, i) => {
                const date = new Date(2021, i, 1);
                return {
                    value: String(i + 1).padStart(2, '0'),
                    name: formatter.format(date)
                };
            });
        } catch (e) {
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return months.map((name, i) => ({
                value: String(i + 1).padStart(2, '0'),
                name: name
            }));
        }
    }

    getWeekDays(locale) {
        try {
            const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
            return Array.from({ length: 7 }, (_, i) => {
                const date = new Date(2021, 0, i + 3); // 2021-01-03 is a Sunday
                return formatter.format(date);
            });
        } catch (e) {
            return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        }
    }

    createCellButton(date) {
        const button = document.createElement('button');
        button.classList.add('btn');
        button.innerText = date.getDate().toString();
        button.style.width = `${this.options.btnWidth}px`;
        button.style.maxWidth = button.style.width;
        button.dataset.date = dateHelper.formatDate(date, 'YYYY-MM-DD');
        button.addEventListener('click', event => {
            const btn = event.target.closest('button') || event.target;
            const _tr = btn.closest('tr');
            const _td = btn.closest('td');
            if (!_tr || !_td) return;
            const _rowIndex = _tr.rowIndex - 1;
            const _cellIndex = _td.cellIndex;

            const calendarEl = btn.closest('.calendar');
            const calendarId = calendarEl ? calendarEl.dataset.calendarId : null;
            const calendar = calendarId
                ? this.calendars.find(_c => _c.id === calendarId)
                : this.calendars.find(_c => {
                    const _y = parseInt(this.inputSelectYear.value);
                    const _m = parseInt(this.inputSelectMonth.value);
                    return _c.id === `${_y}-${(_m <= 9 ? '0' + _m : _m)}`;
                });

            if(calendar && calendar.weeks && calendar.weeks[_rowIndex]) {
                const _day = calendar.weeks[_rowIndex][_cellIndex];
                if(_day && !dateHelper.isSameDate(_day.date, this.selectDate)) {
                    const isDisable = !_day.isCurrentMonth ||
                        dateHelper.isBeforeDate(_day.date, this.minDate) ||
                        dateHelper.isAfterDate(_day.date, this.maxDate) ||
                        this.options.disableDates.includes(dateHelper.formatDate(_day.date, 'YYYY-MM-DD')) ||
                        calendar.disableDates.includes(dateHelper.formatDate(_day.date, 'YYYY-MM-DD'));

                    if(!isDisable && this.options.readOnly === false && btn.classList.contains('active')) {
                        this.selectDate = _day.date;
                        const currentDisplayYear = parseInt(this.inputSelectYear.value);
                        const currentDisplayMonth = parseInt(this.inputSelectMonth.value);
                        this.setCalendar(currentDisplayYear, currentDisplayMonth);
                        if(typeof this.options.events.select === 'function') {
                            this.options.events.select({
                                thisObj : this,
                                params : this.getPayload()
                            });
                        }
                    }
                }
            }
        });
        return button;
    }

    createCalendar(year, month) {
        month = month - 1;
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        // Calculate the number of days to include from the previous month
        const daysFromPrevMonth = firstDay.getDay();
        // Calculate the number of days to include from the next month
        const daysFromNextMonth = (7 - ((daysFromPrevMonth + daysInMonth) % 7)) % 7;
        // Calculate the total number of days to display
        const totalDays = daysFromPrevMonth + daysInMonth + daysFromNextMonth;
        // Calculate the number of weeks
        const numberOfWeeks = Math.ceil(totalDays / 7);
        let currentDay = 1 - daysFromPrevMonth;

        const element = document.createElement('div');
        const caption = document.createElement('div');
        const table = document.createElement('table');
        const thead = document.createElement('thead');
        const tbody = document.createElement('tbody');
        let tr = document.createElement('tr');
        const weeks = [];

        element.classList.add('calendar',  'd-flex',  'flex-column');
        const calendarId = `${year}-${(month+1)<=9 ? ('0'+(month+1).toString()) : (month+1)}`;
        element.dataset.calendarId = calendarId;
        caption.classList.add('caption');
        if (this.options.monthNames[month]) {
            caption.innerText = `${this.options.monthNames[month].name} ${year}`;
        }
        if(this.options.totalCalendar<=1) {
            caption.classList.add('d-none');
        }

        this.options.weekDays.forEach((day) => {
            const th = document.createElement('th');
            th.setAttribute('scope', 'col');
            th.innerText = day;
            tr.appendChild(th);
        })

        thead.appendChild(tr);
        table.appendChild(thead);

        for (let i = 0; i < numberOfWeeks; i++) {
            const week = [];
            tr = document.createElement('tr');
            for (let j = 0; j < 7; j++) {
                const date = new Date(year, month, currentDay);
                const isCurrentMonth = (date.getMonth() === month);
                const td = document.createElement('td');
                const button = this.createCellButton(date);

                td.appendChild(button);
                tr.appendChild(td);
                week.push({
                    date: date,
                    isCurrentMonth: isCurrentMonth,
                    td: td,
                    button: button
                });
                currentDay++;
            }
            weeks.push(week);
            tbody.appendChild(tr);
        }

        table.appendChild(tbody);
        element.appendChild(caption);
        element.appendChild(table);

        return {
            id: calendarId,
            year: year,
            month: (month+1)<=9 ? ('0'+(month+1).toString()) : (month+1),
            firstDay: firstDay,
            lastDay: lastDay,
            disableDates: [],
            element: element,
            weeks: weeks
        }
    }

    setCalendar(year = null, month = null, isPrevious = false) {
        let calendar, _year, _month, _candidate;
        let candidate = [];
        let hasNewCalendar = false;
        let maxWeeks = 0;
        let _reset = (typeof year !== 'number' || typeof month !== 'number');
        if(!_reset) {
            _reset = this.isOutsideRange(year, month);
        }

        if(_reset) {
            this.createSelectYear();
            this.selectMonth(parseInt(this.inputSelectYear.value));
            year = parseInt(this.inputSelectYear.value);
            month = parseInt(this.inputSelectMonth.value);
        }

        this.calendarElement.innerHTML = '';
        for (let i = 0; i < this.options.totalCalendar; i++) {
            _year = year;
            _month = month + (i * (isPrevious? -1 : 1));

            if (_month > 12 && !isPrevious) {
                _month -= 12;
                _year++;
            } else if (_month === 0 && isPrevious) {
                _month = 12;
                _year--;
            }

            calendar = this.calendars.find(_calendar => _calendar.id === `${_year}-${(_month<=9? ('0'+_month.toString()) : _month.toString())}`);
            if(!calendar) {
                calendar = this.createCalendar(_year, _month);
                this.calendars.push(calendar);
                hasNewCalendar = true;
            }

            candidate.push(calendar);
        }

        if(this.options.totalCalendar>=2 && hasNewCalendar) {
            this.calendars.forEach(_calendar => {
                maxWeeks = Math.max(maxWeeks, _calendar.weeks.length);
            });
            this.calendars.forEach(_calendar => {
                this.adjustCalendarWeeks(_calendar, maxWeeks);
            });
        }

        candidate.forEach(_calendar => {
            this.setWeeks(_calendar);
            this.calendarElement.appendChild(_calendar.element);
        });

        this.inputSelectYear.value = (isPrevious?_year:year);
        this.selectMonth((isPrevious?_year:year));
        this.inputSelectMonth.value = ((isPrevious?_month:month)<=9? ('0'+(isPrevious?_month:month).toString()) : (isPrevious?_month:month).toString());
        this.buttonPrev.disabled = this.isLowerRange(year, month, true);
        this.buttonNext.disabled = this.isUpperRange(year, month, true);
        this.calendars.sort((a, b) => a.id.localeCompare(b.id));
    }

    adjustCalendarWeeks(calendar, targetWeeks) {
        let button;
        const currentWeeks = calendar.weeks.length;
        if (currentWeeks < targetWeeks) {
            const lastWeek = calendar.weeks[currentWeeks - 1];
            const nextMonthStart = new Date(lastWeek[6].date);
            nextMonthStart.setDate(nextMonthStart.getDate() + 1);

            for (let i = currentWeeks; i < targetWeeks; i++) {
                const newWeek = [];
                const tr = document.createElement('tr');
                for (let j = 0; j < 7; j++) {
                    const td = document.createElement('td');
                    const newDate = new Date(nextMonthStart);
                    newDate.setDate(newDate.getDate() + (7 * (i - currentWeeks) + j));
                    button = this.createCellButton(newDate);
                    td.appendChild(button);
                    tr.appendChild(td);
                    newWeek.push({
                        date: newDate,
                        isCurrentMonth: false,
                        td: td,
                        button: button
                    });
                }
                calendar.weeks.push(newWeek);
                calendar.element.children[1].children[1].appendChild(tr);
            }
        }
    }

    setWeeks(calendar) {
        calendar.weeks.forEach((week) => {
            week.forEach((day) => {
                day.button.classList.remove('selected');
                day.button.classList.remove('active');

                const dateStr = dateHelper.formatDate(day.date, 'YYYY-MM-DD');
                day.button.disabled = !day.isCurrentMonth ||
                    dateHelper.isBeforeDate(day.date, this.minDate) ||
                    dateHelper.isAfterDate(day.date, this.maxDate) ||
                    this.options.disableDates.includes(dateStr) ||
                    calendar.disableDates.includes(dateStr);

                if(!day.button.disabled  && this.options.readOnly === false) {
                    if(dateHelper.isSameDate(day.date, this.selectDate)) {
                        day.button.classList.add('selected');
                    } else {
                        day.button.classList.add('active');
                    }
                }
            })
        });
    }

    resetCalendar() {
        this.createSelectYear();
        this.selectMonth(parseInt(this.inputSelectYear.value));
        this.setCalendar(parseInt(this.inputSelectYear.value), parseInt(this.inputSelectMonth.value));
    }

    isLowerRange(year, month, isLowerEqual = false) {
        const _min = parseInt(dateHelper.formatDate(this.minDate, 'YYYYMM'));
        const _yearmonth = parseInt(year.toString() + (month<=9? ('0'+month.toString()) : month.toString()));
        return isLowerEqual ?(_yearmonth <= _min):(_yearmonth < _min)
    }

    isUpperRange(year, month, isUpperEqual = false) {
        const _max = parseInt(dateHelper.formatDate(this.maxDate, 'YYYYMM'));
        const _yearmonth = parseInt(year.toString() + (month<=9? ('0'+month.toString()) : month.toString()));
        return isUpperEqual?(_yearmonth >= _max):(_yearmonth > _max)
    }

    isOutsideRange(year, month) {
        return this.isLowerRange(year, month) || this.isUpperRange(year, month);
    }

    createController() {
        const controller = document.createElement('div');
        const elementPicker = document.createElement('div');
        const epChildren = document.createElement('div');
        const _epcMonth = document.createElement('div');
        const _epcYear = document.createElement('div');
        const elementButton = document.createElement('div');

        elementPicker.classList.add('picker');
        epChildren.classList.add('d-flex', 'flex-nowrap', 'justify-content-between', 'align-items-center');
        _epcMonth.classList.add('month', 'flex-grow-1');
        _epcYear.classList.add('year', 'flex-shrink-0', 'ms-1');

        _epcMonth.appendChild(this.inputSelectMonth);
        _epcYear.appendChild(this.inputSelectYear);

        epChildren.appendChild(_epcMonth);
        epChildren.appendChild(_epcYear);
        elementPicker.appendChild(epChildren);

        elementButton.classList.add('buttons', 'flex-shrink-0', 'ps-2', 'text-end');
        this.buttonNext = document.createElement('button');
        this.buttonPrev = document.createElement('button');
        this.buttonPrev.classList.add('btn', 'prev', 'me-1');
        this.buttonPrev.innerHTML = '<i class="bi bi-chevron-left"></i>';
        this.buttonPrev.addEventListener('click', event => {
            let year = parseInt(this.inputSelectYear.value);
            let month = parseInt(this.inputSelectMonth.value);

            if(month===1) {
                month = 12;
                year -= 1;
            } else {
                month -= 1;
            }

            if(!this.isLowerRange(year, month)) {
                this.setCalendar(year, month);
                if(typeof this.options.events.previous === 'function') {
                    this.options.events.previous({
                        thisObj : this,
                        params : this.getPayload()
                    });
                }
            }
        });


        this.buttonNext.classList.add('btn', 'next');
        this.buttonNext.innerHTML = '<i class="bi bi-chevron-right"></i>';
        this.buttonNext.addEventListener('click', event => {
            let year = parseInt(this.inputSelectYear.value);
            let month = parseInt(this.inputSelectMonth.value);

            if(month===12) {
                month = 1;
                year += 1;
            } else {
                month += 1;
            }

            if(!this.isUpperRange(year, month)) {
                this.setCalendar(year, month);
                if(typeof this.options.events.next === 'function') {
                    this.options.events.next({
                        thisObj : this,
                        params : this.getPayload()
                    });
                }
            }
        });

        elementButton.appendChild(this.buttonPrev);
        elementButton.appendChild(this.buttonNext);


        controller.classList.add('controller', 'd-flex', 'flex-nowrap', 'justify-content-between', 'align-items-center');
        controller.appendChild(elementPicker);
        controller.appendChild(elementButton);
        return controller;
    }

    createSelectYear(minYear = null) {
        if (this.inputSelectYear) {
            this.inputSelectYear.innerHTML = '';
        } else {
            this.inputSelectYear = document.createElement('select');
            this.inputSelectYear.classList.add('form-select');
            this.inputSelectYear.addEventListener('change', (e) => {
                const newYear = parseInt(e.target.value);
                this.selectMonth(newYear);
                this.setCalendar(newYear, parseInt(this.inputSelectMonth.value));
                if(typeof this.options.events.change === 'function') {
                    this.options.events.change({
                        thisObj : this,
                        params : this.getPayload()
                    });
                }
            });
        }

        this.minYear = (minYear?minYear:this.currentDate.getFullYear());
        if(this.options.minDate && dateHelper.isDate(this.options.minDate)) {
            this.minYear = dateHelper.toDate(this.options.minDate).getFullYear();
            if(minYear) {
                if(minYear > this.minYear) {
                    this.minYear = minYear;
                }
            }
        }

        this.maxYear = this.minYear + this.options.totalYear;    // 10 years after current year
        if(this.options.maxDate && dateHelper.isDate(this.options.maxDate)) {
            const optMaxYear = dateHelper.toDate(this.options.maxDate).getFullYear();
            if (optMaxYear > this.maxYear) {
                this.maxYear = optMaxYear;
            } else {
                this.maxYear = Math.max(this.minYear, optMaxYear);
            }
            if(minYear && this.options.minDate && dateHelper.isDate(this.options.minDate)) {
                if(this.minYear > this.maxYear) {
                    this.maxYear = this.minYear;
                }
            }
        }

        for (let year = this.minYear; year <= this.maxYear; year++) {
            const option = document.createElement('option');
            option.value = year;
            option.textContent = year;
            this.inputSelectYear.appendChild(option);
        }

        // Set the current year as selected
        this.inputSelectYear.value = this.minYear;
    }

    selectMonth(year = null) {
        const currentYear = this.currentDate.getFullYear();
        const currentMonth = this.currentDate.getMonth();

        if (this.inputSelectMonth) {
            // Clear existing options
            this.inputSelectMonth.innerHTML = '';
        } else {
            this.inputSelectMonth = document.createElement('select');
            this.inputSelectMonth.classList.add('form-select');

            // Add event listener for month change only when creating the select for the first time
            this.inputSelectMonth.addEventListener('change', (e) => {
                this.setCalendar(parseInt(this.inputSelectYear.value), parseInt(e.target.value));
                if(typeof this.options.events.change === 'function') {
                    this.options.events.change({
                        thisObj : this,
                        params : this.getPayload()
                    });
                }
            });
        }

        let startMonth = 0;
        let endMonth = 11;
        // let reInitMonthYear = false;

        this.minDate = dateHelper.toDate(dateHelper.addDay(this.currentDate, this.options.addDay));
        if(this.options.minDate && dateHelper.isDate(this.options.minDate)) {
            this.minDate = dateHelper.toDate(this.options.minDate);
        }

        this.maxDate = dateHelper.toDate(new Date(this.maxYear, 11, 31)); // December 31st of maxYear
        if(this.options.maxDate && dateHelper.isDate(this.options.maxDate)) {
            const optMaxDate = dateHelper.toDate(this.options.maxDate);
            this.maxDate = optMaxDate;
        }

        if (year === this.minDate.getFullYear()) {
            startMonth = this.minDate.getMonth();
            if(this.options.disableDates.length) {
                let reInitMonthYear;
                while (this.options.disableDates.includes(dateHelper.formatDate(this.minDate, 'YYYY-MM-DD'))) {
                    this.minDate = dateHelper.toDate(dateHelper.addDay(this.minDate, 1));
                    reInitMonthYear = true;
                }

                if (reInitMonthYear) {
                    // Reinitialize minYear and maxYear based on the new minDate
                    this.minYear = this.minDate.getFullYear();
                    // this.maxYear = this.minYear + this.options.totalYear;

                    // Recreate the year select with the new range
                    this.createSelectYear(this.minYear);
                    if(!(this.options.maxDate && dateHelper.isDate(this.options.maxDate))) {
                        this.maxDate = dateHelper.toDate(new Date(this.maxYear, 11, 31));
                    } else {
                        this.maxDate = dateHelper.toDate(this.options.maxDate);
                    }

                    // Set the year to the new minYear
                    year = this.minYear;

                    // Reinitialize the month options
                    startMonth = this.minDate.getMonth();
                }
            }

            if(!this.selectDate) {
                this.selectDate = this.minDate;
            }
        } else if (year < this.minDate.getFullYear()) {
            // If the selected year is before minDate, update minYear and maxYear
            this.minYear = this.minDate.getFullYear();
            // this.maxYear = this.minYear + this.options.totalYear;
            this.createSelectYear(this.minYear); // Recreate the year select with new range
            this.selectMonth(this.minYear); // Recursively call with new minYear
            return;
        }

        if (year === this.maxDate.getFullYear()) {
            endMonth = this.maxDate.getMonth();
        }

        for (let i = startMonth; i <= endMonth; i++) {
            if (this.options.monthNames[i]) {
                const option = document.createElement('option');
                option.value = this.options.monthNames[i].value;
                option.textContent = this.options.monthNames[i].name;
                this.inputSelectMonth.appendChild(option);
            }
        }

        // Set the appropriate month as selected
        if (year === currentYear && currentMonth >= startMonth && currentMonth <= endMonth) {
            this.inputSelectMonth.value = this.options.monthNames[currentMonth].value;
        } else {
            if (this.options.monthNames[startMonth]) {
                this.inputSelectMonth.value = this.options.monthNames[startMonth].value;
            }
        }
    }

    setOptions(options) {
        this.options = {...this.options,...options};
        this.resetCalendar();
    }

    setMinDate(date) {
        this.options.minDate = date;
        this.resetCalendar();
    }

    setMaxDate(date) {
        this.options.maxDate = date;
        this.resetCalendar();
    }

    setAddDay(total) {
        this.options.addDay = typeof total === 'number'? total : (isNaN(parseInt(total))? 0 : parseInt(total));
        this.resetCalendar();
    }

    setReadOnly(isReadOnly = true) {
        const _year = parseInt(this.inputSelectYear.value);
        const _month = parseInt(this.inputSelectMonth.value);
        this.options.readOnly = isReadOnly;
        this.setCalendar(_year, _month);
    }

    setSelectDate(date) {
        const _year = parseInt(this.inputSelectYear.value);
        const _month = parseInt(this.inputSelectMonth.value);
        if(date) {
            if(dateHelper.isDate(date)) {
                date = dateHelper.isDateInstance(date) ? date : dateHelper.toDate(date);
                if(dateHelper.isBetweenDate(date, this.minDate, this.maxDate)) {
                    this.selectDate = date;
                } else {
                    this.selectDate = null;
                }
            } else {
                this.selectDate = null;
            }
        } else {
            this.selectDate = null;
        }
        this.setCalendar(_year, _month);
    }

    getPayload() {
        return {
            date: this.selectDate,
            year: parseInt(this.inputSelectYear.value),
            month: parseInt(this.inputSelectMonth.value),
            minYear: this.minYear,
            maxYear: this.maxYear,
            minDate: this.minDate,
            maxDate: this.maxDate,
        }
    }

    pushDisableDates(dates) {
        if(Array.isArray(dates) && dates.length > 0) {
            dates.forEach((date) => {
                if(!this.options.disableDates.includes(date)) {
                    this.options.disableDates.push(date);
                }
            });
        }
    }
}

export default DtaCalendar;
